import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { RadioButton } from '@enechain/ecloud-designsystem'

import { DsSelect, type DsSelectOption } from '~/components/common/DsSelect'
import { IndeterminateCheckboxGroup } from '~/components/common/filters/IndeterminateCheckboxGroup'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'

type SelectProps = {
  variant: 'select'
  value: AreaCode | undefined
  onChange: (value: AreaCode | undefined) => void
  areaCodes?: readonly AreaCode[]
  allLabel?: string
  ariaLabel?: string
  width?: string
}

type CheckboxProps = {
  variant: 'checkbox'
  value: Set<AreaCode>
  onChange: (value: Set<AreaCode>) => void
  areaCodes?: readonly AreaCode[]
  allLabel?: string
}

type RadioProps = {
  variant: 'radio'
  value: AreaCode | undefined
  onChange: (value: AreaCode | undefined) => void
  areaCodes?: readonly AreaCode[]
  allLabel?: string
}

type AreaFilterProps = SelectProps | CheckboxProps | RadioProps

export const AreaFilter = (props: AreaFilterProps): React.ReactElement => {
  const { areaCodes = JEPX_AREA_CODES } = props

  switch (props.variant) {
    case 'select':
      return <AreaSelectFilter {...props} areaCodes={areaCodes} />
    case 'checkbox':
      return <AreaCheckboxFilter {...props} areaCodes={areaCodes} />
    case 'radio':
      return <AreaRadioFilter {...props} areaCodes={areaCodes} />
  }
}

const AreaSelectFilter = ({
  value,
  onChange,
  areaCodes,
  allLabel,
  ariaLabel,
  width = '150px',
}: SelectProps & { areaCodes: readonly AreaCode[] }): React.ReactElement => {
  const { t } = useTranslation()

  const options: DsSelectOption[] = useMemo(
    () => [
      { value: '', label: allLabel ?? t('common:area.all') },
      ...areaCodes.map((areaCode) => ({
        value: String(areaCode),
        label: convertAreaLabel(areaCode),
      })),
    ],
    [areaCodes, allLabel, t],
  )

  return (
    // 単一 select は共通の `DsSelect` を通す (UI 辞書 dictionary/components.md `.ecl-select` 行
    // 「dev は `DsSelect` / `FormSelect` 経由で共通適用」)。 font 補正・body への portal・
    // modal 内でも選択肢が押せる対策 (pointerEvents) が DsSelect 側に集約されている。
    // 「すべて」 は value="" の実在の選択肢なので placeholder ではない (常に選択中の値がある)。
    <DsSelect
      minWidth={width}
      aria-label={ariaLabel}
      onChange={(raw) => {
        if (raw === undefined || raw === '') {
          onChange(undefined)
          return
        }
        const num = Number(raw)
        if (!Number.isNaN(num)) {
          onChange(num as AreaCode)
        }
      }}
      options={options}
      value={value === undefined ? '' : String(value)}
    />
  )
}

const AreaCheckboxFilter = ({
  value,
  onChange,
  areaCodes,
  allLabel,
}: CheckboxProps & { areaCodes: readonly AreaCode[] }): React.ReactElement => {
  const { t } = useTranslation()

  const items = useMemo(
    () =>
      areaCodes.map((code) => ({ value: code, label: convertAreaLabel(code) })),
    [areaCodes],
  )

  const handleToggle = (code: AreaCode): void => {
    const next = new Set(value)
    if (next.has(code)) {
      next.delete(code)
    } else {
      next.add(code)
    }
    onChange(next)
  }

  const handleToggleAll = (): void => {
    if (areaCodes.every((code) => value.has(code))) {
      onChange(new Set())
    } else {
      onChange(new Set(areaCodes))
    }
  }

  return (
    <IndeterminateCheckboxGroup
      items={items}
      masterLabel={allLabel ?? t('common:area.all')}
      onToggle={handleToggle}
      onToggleAll={handleToggleAll}
      selected={value}
    />
  )
}

const AreaRadioFilter = ({
  value,
  onChange,
  areaCodes,
  allLabel,
}: RadioProps & { areaCodes: readonly AreaCode[] }): React.ReactElement => {
  const { t } = useTranslation()

  const radios = useMemo(
    () => [
      { value: '', label: allLabel ?? t('common:area.all') },
      ...areaCodes.map((code) => ({
        value: String(code),
        label: convertAreaLabel(code),
      })),
    ],
    [areaCodes, allLabel, t],
  )

  return (
    <RadioButton
      direction="row"
      onValueChange={(details) => {
        if (details.value === '') {
          onChange(undefined)
        } else {
          onChange(Number(details.value) as AreaCode)
        }
      }}
      radios={radios}
      value={value === undefined ? '' : String(value)}
    />
  )
}
